import { ChangeEventWorkflow } from "src/generated/graphqlTypes";
import { changeEventQueryFor, GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";
import { ReviewImpactStep } from "src/routes/projects/change-events/guided/workflows/sharedSteps/ReviewImpactStep";
import {
  reconcileImpactedCommitmentsStep,
  reviewConfigurationStep,
  reviewStep,
} from "src/routes/projects/change-events/guided/workflows/sharedSteps/sharedSteps";
import { useMaybeOpenCommitmentCleanupModal } from "src/routes/projects/change-events/guided/workflows/sharedSteps/useMaybeOpenCommitmentCleanupModal";
import { useReviewStepActions } from "src/routes/projects/change-events/guided/workflows/sharedSteps/useReviewStepActions";
import { defineWorkflow } from "src/routes/projects/change-events/guided/workflows/types";
import { SelectConfigurationStep } from "../1.SelectConfigurationStep";
import {
  applyConfigurationFormConfig,
  ApplyConfigurationFormInput,
  applyConfigurationMapToForm,
} from "../applyConfigurationForm";
import {
  useApplyConfigurationActions,
  useSaveApplyConfigurationChangeEvent,
} from "../useSaveApplyConfigurationChangeEvent";
import { SelectChangesStep } from "./2.SelectChangesStep";

export const applyPartialConfigurationWorkflow = defineWorkflow<ApplyConfigurationFormInput>({
  workflow: ChangeEventWorkflow.ApplyPartialConfiguration,
  formConfig: applyConfigurationFormConfig,
  mapToForm: applyConfigurationMapToForm,
  steps: [
    {
      value: GuidedChangeEventStep.SelectConfiguration,
      label: "Select New Config",
      isComplete: (form) => !!form.targetConfigId.value,
      render: (form, project) => <SelectConfigurationStep formState={form} project={project} />,
    },
    {
      value: GuidedChangeEventStep.SelectChanges,
      label: "Select Changes",
      isComplete: (form) => form.optionIds.valid,
      disabled: (form) => !form.configChangeId.value,
      render: (form) => <SelectChangesStep formState={form} />,
    },
    reviewConfigurationStep(),
    {
      value: GuidedChangeEventStep.ReviewImpact,
      label: "Review Impact",
      isComplete: (form) => !!form.targetConfigId.value && form.optionIds.valid,
      disabled: (form) => !changeEventQueryFor(form).changeEventId || form.optionIds.dirty,
      render: (form) => <ReviewImpactStep formState={form} checkoutConfigId={form.targetConfigId.value} />,
    },
    reconcileImpactedCommitmentsStep(),
    reviewStep(),
  ],
  // Resuming a saved change event whose configuration is already picked starts on its impact, like swap product,
  // unless its picks still need re-deriving, which only Select Changes' Continue does.
  initialStep: ({ form }) =>
    form.id.value && form.targetConfigId.value && form.optionIds.valid && !form.needsReconcile.value
      ? GuidedChangeEventStep.ReviewImpact
      : GuidedChangeEventStep.SelectConfiguration,
  useActions: (args) => {
    const actions = useApplyConfigurationActions(args);
    const { saveChangeEvent } = useSaveApplyConfigurationChangeEvent(args);
    const { submit } = useReviewStepActions(args);
    // Removing or changing options rewrites scope that may already be on a purchase order, and the backend
    // won't touch a live PO, so submitting is gated on those being cleaned up by hand first.
    const { submitWithCommitmentCleanup } = useMaybeOpenCommitmentCleanupModal({
      form: args.form,
      onSubmit: submit,
      onSaveDraftAndExit: () => saveChangeEvent({ andExit: true }),
    });
    return { ...actions, onSubmit: submitWithCommitmentCleanup };
  },
});
